<template>
  <div v-if="intent">
    <div class="px-4 mb-3">
      <portal-payment-method-selector
        v-model="method"
        :allowed="allowedMethods"
        :card-fee="intent.card_processing_fee_rate"
      />
    </div>

    <div class="px-4 mb-3">
      <div
        v-if="message"
        class="border bg-red-50 border-red-600 text-red-600 p-4 rounded"
      >
        {{ message }}
      </div>
    </div>

    <form
      @submit.prevent="charge"
      class="block px-4 py-2"
      v-if="!settingUp"
    >
      <!-- Stripe payment element will render here -->
      <!-- eslint-disable-next-line vue/no-unused-refs -->
      <div
        id="payment-element"
        ref="payment"
      />

      <button
        type="submit"
        class="mt-4 flex w-full items-center justify-center space-x-2 font-medium shadow py-3 px-2 rounded-lg"
        :disabled="disableSubmit"
        :class="[
          !loading && !disableSubmit ? 'cursor-pointer bg-primary-600 text-white hover:bg-primary-700' : '',
          loading || disableSubmit ? 'cursor-not-allowed bg-primary-600 text-white' : '',
        ]"
      >
        <span
          class="inline-flex"
          v-if="!loading"
        >
          <ion-icon name="lock-closed" />
        </span>
        <span
          class="inline-flex"
          v-else
        >
          <client-loading-indicator
            :styled="false"
            class="fill-white text-primary-700 stroke-primary-900"
          />
        </span>
        <span v-if="!loading">{{ amountBtnText }}</span>
      </button>
    </form>

    <div
      v-else
      class="flex justify-center py-4"
    >
      <client-loading-indicator class="text-primary-500 fill-gray-100 w-5 h-5" />
    </div>
  </div>
</template>

<script>
import Payments from "../../repository/PortalPayments"
import currencyUtils from "../../../js/helpers/currencyUtils"
import ClientLoadingIndicator from "../../client_auth/components/client-loading-indicator.vue"
import PortalPaymentMethodSelector from "./PortalPaymentMethodSelector.vue"

export default {
  name: "PortalStripePaymentForm",

  components: { PortalPaymentMethodSelector, ClientLoadingIndicator },

  props: {
    invoice: { required: true, type: Object },
    paymentToken: { required: false, type: String, default: null },
    amountBtnText: { required: true, type: String },
    amtFieldFocused: { required: false, type: Boolean, default: false },
    intentAmount: { required: true, type: Number },
    amountBelowMinPaymentAmount: { required: false, default: false, type: Boolean },
    amountAboveMaxPaymentAmount: { required: false, default: false, type: Boolean },
    paymentAmountSelection: { required: false, default: "full_amount", type: String },
  },

  emits: ["approved", "intent", "loaded", "method-changed"],

  data() {
    return {
      intent: null,
      loading: false,
      disabled: false,
      method: "CARD",
      settingUp: true,
      stripe: null,
      stripeElement: null,
      message: null,
    }
  },

  computed: {
    allowedMethods() {
      return this.invoice.payment_methods
        .map((item) => (item.toLowerCase() === "cc" ? "CARD" : "ACH"))
        .sort()
        .reverse()
    },

    amount() {
      return currencyUtils.convertAndFormatCurrency(this.intent.amount_due)
    },

    disableSubmit() {
      return (
        this.amtFieldFocused ||
        this.loading ||
        this.disabled ||
        this.amountBelowMinPaymentAmount ||
        this.amountAboveMaxPaymentAmount
      )
    },
  },

  watch: {
    method() {
      this.$emit("method-changed", this.method)
      this.loadPaymentIntent()
    },

    amtFieldFocused(newValue) {
      // return early if amount is below minimum payment amount or if partial payments are not allowed or its above the max payment amount
      if (
        this.amountBelowMinPaymentAmount ||
        !this.invoice.allow_partial_payments ||
        this.amountAboveMaxPaymentAmount
      ) {
        return
      }
      if (!newValue) {
        this.loadPaymentIntent()
      }
    },

    paymentAmountSelection() {
      this.loadPaymentIntent()
    },
  },

  beforeMount() {
    if (this.allowedMethods.includes("CARD")) {
      this.method = "CARD"
    } else {
      this.method = "ACH"
    }
    this.$emit("method-changed", this.method)
  },

  mounted() {
    this.$emit("loaded", true)
    this.loadPaymentIntent()
  },

  methods: {
    async loadPaymentIntent() {
      if (this.$route.query.payment_intent_client_secret) {
        this.checkIntentStatus()

        return
      }

      this.settingUp = true
      try {
        const intentAmount =
          !this.invoice.allow_partial_payments || this.paymentAmountSelection === "full_amount"
            ? null
            : currencyUtils.convertToCents(this.intentAmount)
        const { data } = await Payments.getStripePaymentIntent(this.invoice, this.method, intentAmount)
        this.intent = data.data
        this.$emit("intent", data.data)
      } catch (e) {
        if (e.response?.status === 403) {
          this.$alert("You are not authorized to complete this action.")
        } else {
          this.$alert("Unable to complete your request. Please try refreshing the page and try again.")
        }
        console.error(e)
      }

      this.settingUp = false
      this.loading = false

      this.$nextTick(() => {
        this.setUpStripeForm()
      })
    },

    resetPaymentForm() {
      const { query } = this.$route

      if (query.payment_intent_client_secret) {
        delete query.payment_intent_client_secret
      }

      this.$router
        .replace({
          query: {
            ...query,
          },
        })
        .catch(() => {
          // ignore duplicate route error
        })
    },

    async checkIntentStatus() {
      this.settingUp = true

      try {
        const { data } = await axios.get(route("client-portal.stripe.initialize", { invoice: this.invoice }), {
          params: { payment_token: this.invoice.payment_token },
        })

        const secret = this.$route.query.payment_intent_client_secret
        // eslint-disable-next-line no-undef
        const stripe = Stripe(window.FinancialCents.stripe, {
          stripeAccount: data.data.account_id,
        })

        stripe.retrievePaymentIntent(secret).then((response) => {
          if (response.error) {
            this.resetPaymentForm()
            return
          }

          const paymentIntent = response.paymentIntent

          // Inspect the PaymentIntent `status` to indicate the status of the payment
          // to your customer.
          //
          // Some payment methods will [immediately succeed or fail][0] upon
          // confirmation, while others will first enter a `processing` state.
          //
          // [0]: https://stripe.com/docs/payments/payment-methods#payment-notification
          switch (paymentIntent.status) {
            case "succeeded":
              this.message = "Success! Payment received."
              this.approved()
              this.resetPaymentForm()
              break

            case "processing":
              this.message = "Payment processing. We'll update you when payment is received."
              this.approved()
              this.resetPaymentForm()
              break

            case "requires_payment_method":
              this.message = "Payment failed. Please try another payment method."
              // Redirect your user back to your payment page to attempt collecting
              // payment again
              this.resetPaymentForm()
              break

            default:
              this.message = "Something went wrong."
              this.resetPaymentForm()
              break
          }
        })
      } catch (e) {
        console.error(e)
      }
      this.settingUp = false
    },

    setUpStripeForm() {
      if (!this.intent) {
        return
      }

      // eslint-disable-next-line no-undef
      this.stripe = Stripe(window.FinancialCents.stripe, {
        stripeAccount: this.intent.account_id,
      })

      const options = {
        clientSecret: this.intent.client_secret,
        // Fully customizable with the Appearance API
        appearance: {
          /*...*/
        },
      }

      // Set up Stripe.js and Elements to use in checkout form using the client secret
      this.stripeElement = this.stripe.elements(options)

      // Create and mount the Payment Element
      const element = this.stripeElement.create("payment")
      element.mount("#payment-element")
    },

    /**
     * Verify that the payment intent is still valid before submitting payment.
     *
     * @returns {Promise<void>}
     */
    async charge() {
      this.loading = true
      try {
        const paymentAmount =
          !this.invoice.allow_partial_payments || this.paymentAmountSelection === "full_amount"
            ? null
            : this.intent.amount_due
        const { data } = await axios.get(route("client-portal.stripe.can-pay", this.invoice), {
          params: {
            payment_token: this.invoice.payment_token || this.paymentToken,
            payment_method_type: this.method,
            payment_amount: paymentAmount,
          },
        })

        await this.submitPayment()
      } catch (e) {
        console.error(e)
        window.location.refresh()
      }
    },

    async submitPayment() {
      if (!this.stripe) {
        return
      }

      const { error, paymentIntent } = await this.stripe.confirmPayment({
        //`Elements` instance that was used to create the Payment Element
        elements: this.stripeElement,
        // Remove return_url and use redirect: 'if_required' to avoid unnecessary page reloads
        redirect: "if_required", // Only redirect if absolutely necessary (3D Secure, etc.)
      })

      if (error) {
        // This point will only be reached if there is an immediate error when
        // confirming the payment. Shows error to user
        this.message = error.message
        this.loading = false
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        // Payment succeeded immediately - no redirect needed
        this.message = "Success! Payment received."
        this.approved()
      } else if (paymentIntent && paymentIntent.status === "processing") {
        // Payment is processing (e.g., bank transfer)
        this.message = "Payment processing. We'll update you when payment is received."
        this.approved()
      } else {
        // Payment requires additional action (like 3D Secure)
        // Stripe will handle the redirect automatically if needed
        this.message = "Completing payment..."
      }
    },

    approved() {
      this.$emit("approved", {
        detail: [{ status: "SUCCESS" }],
      })
      this.loading = false
    },
  },
}
</script>
